import { useMemo } from 'react';
import { debounce } from 'lodash';
import type { ApolloClient } from '@apollo/client';
import { useIsFlattenFoldersEnabled } from '@frameio/assets/shared';
import { useApolloClient } from '@frameio/graphql-client';
import { useActiveFolderId } from '@frameio/shared/contexts';
import type { FinderRefetchOptions } from '@frameio/shared/contexts/FinderRefetchContext';
import { useGetEphemeralFilters } from '@frameio/shared/finder/hooks/useGetEphemeralFilters';
import { useCurrentPageType } from '@frameio/shared/routing';
import { getRefetchCollectionAssetsOptions } from './getRefetchCollectionAssetsOptions';
import { getRefetchFinderItemsOptions } from './getRefetchFinderItemsOptions';
import { getRefetchFolderAssetsOptions } from './getRefetchFolderAssetsOptions';
import { getRefetchTrashAssetsOptions } from './getRefetchTrashAssetsOptions';

/**
 * useTemporaryFinderRefetch
 *
 * This hook returns a refetch callback. It exists temporarily as a single point
 * of entry for Finder 1 and Finder 2. It will be removed once Finder 1 is
 * removed.
 *
 * @todo (FIND-948): remove this thing
 */

export function useTemporaryFinderRefetch() {
  const client = useApolloClient();
  const activeFolderId = useActiveFolderId();
  const { ephemeralFilters } = useGetEphemeralFilters();
  const flattenFolders = useIsFlattenFoldersEnabled();
  const currentPage = useCurrentPageType();

  const shouldUseCollectionQueryForFolders =
    flattenFolders || ephemeralFilters?.length > 0;

  return useMemo(() => {
    return debounce(
      ({ extraInclude, ...options }: FinderRefetchOptions = {}) => {
        const observedQueryNames = new Set<string>();
        client.getObservableQueries('active').forEach((query) => {
          if (query?.queryName) observedQueryNames.add(query.queryName);
        });

        let finderItemsOpts = getRefetchFinderItemsOptions(
          observedQueryNames,
          options
        );
        let collectionAssetsOpts = getRefetchCollectionAssetsOptions(
          client,
          observedQueryNames,
          options
        );
        let folderAssetsOpts = shouldUseCollectionQueryForFolders
          ? collectionAssetsOpts
          : getRefetchFolderAssetsOptions(
              client,
              observedQueryNames,
              activeFolderId,
              options
            );
        const trashAssetsOpts = getRefetchTrashAssetsOptions(
          client,
          observedQueryNames,
          activeFolderId,
          options
        );

        if (extraInclude) {
          const finderItemsInclude =
            typeof finderItemsOpts.include === 'string'
              ? [finderItemsOpts.include, ...extraInclude]
              : [...(finderItemsOpts.include ?? []), ...extraInclude];
          finderItemsOpts = {
            ...finderItemsOpts,
            include: finderItemsInclude,
          };

          const collectionAssetsInclude =
            typeof collectionAssetsOpts.include === 'string'
              ? [collectionAssetsOpts.include, ...extraInclude]
              : [...(collectionAssetsOpts.include ?? []), ...extraInclude];
          collectionAssetsOpts = {
            ...collectionAssetsOpts,
            include: collectionAssetsInclude,
          };

          const folderAssetsInclude =
            typeof folderAssetsOpts.include === 'string'
              ? [folderAssetsOpts.include, ...extraInclude]
              : [...(folderAssetsOpts.include ?? []), ...extraInclude];
          folderAssetsOpts = {
            ...folderAssetsOpts,
            include: folderAssetsInclude,
          };
        }
        refetchHelper(client, finderItemsOpts);
        refetchHelper(client, collectionAssetsOpts);
        if (currentPage === 'trash') {
          refetchHelper(client, trashAssetsOpts);
        } else {
          refetchHelper(client, folderAssetsOpts);
        }
      },

      DEFAULT_DEBOUNCE,
      { leading: true }
    );
  }, [activeFolderId, client, currentPage, shouldUseCollectionQueryForFolders]);
}

const refetchHelper = (client: ApolloClient<any>, options: any) => {
  client.refetchQueries(options);
};

const DEFAULT_DEBOUNCE = 1000;
